<template>
  <div class="auth-container" :class="{ 'theme-light': !isDarkTheme }">
    <div class="auth-left">
      <div class="auth-wrapper">
        <div class="auth-content">
          <!-- Logo e Branding -->
          <div class="auth-header">
            <div class="logo-container">
              <a href="/" class="logo-link">
                <mx3-logo/>
              </a>
            </div>
            <p class="brand-subtitle">Acesse sua conta</p>
          </div>

          <!-- Formulário de Login -->
          <form @submit.prevent="onSubmit" class="auth-form">
            <div class="form-group">
              <label for="email" class="form-label">E-mail</label>
              <div class="input-wrapper">
                <input 
                  id="email"
                  type="email" 
                  v-model="email" 
                  placeholder="seu@email.com" 
                  class="form-input"
                  required 
                />
                <span class="input-icon">✉</span>
              </div>
            </div>

            <div class="form-group">
              <label for="password" class="form-label">Senha</label>
              <div class="input-wrapper">
                <input 
                  id="password"
                  :type="show ? 'text' : 'password'" 
                  v-model="password" 
                  placeholder="••••••••" 
                  class="form-input"
                  required 
                />
                <span class="input-icon">🔒</span>
                <button 
                  type="button" 
                  class="toggle-password"
                  @click="show = !show"
                  :title="show ? 'Ocultar' : 'Mostrar'"
                >
                  {{ show ? '👁️' : '👁️‍🗨️' }}
                </button>
              </div>
            </div>

            <!-- Placeholder para Recaptcha -->
            <div class="recaptcha-placeholder" v-if="recaptchaVisible">
              <div class="g-recaptcha" data-sitekey="SUA_SITE_KEY"></div>
            </div>

            <!-- Mensagem de Erro -->
            <div v-if="msg" class="alert alert-error">
              <span class="alert-icon">⚠️</span>
              <span class="alert-text">{{ msg }}</span>
            </div>

            <!-- Botão de Login -->
            <button 
              type="submit" 
              class="btn-login"
              :disabled="loading"
            >
              <span v-if="!loading" class="btn-text">Entrar na Conta</span>
              <span v-else class="btn-text loading">
                <span class="spinner"></span> Conectando...
              </span>
            </button>
          </form>

          <!-- Links Adicionais -->
          <div class="auth-footer">
            <a href="/recovery" class="link-forgot">Esqueci minha senha</a>
            <span class="divider">•</span>
            <a href="/register" class="link-register">Criar conta</a>
          </div>

          <!-- Login Social -->
          <div class="social-login-container">
            <GoogleLogin class="google-btn-custom"/>
          </div>
        </div>
      </div>
    </div>

    <!-- Lado Direito - Imagem -->
    <div class="auth-right">
      <div class="bg-image"></div>
      <div class="overlay-gradient"></div>
    </div>
  </div>
</template>

<script>
import GoogleLogin from "@/components/GoogleLogin.vue"
export default {
  name: "Login",
  components: {
    GoogleLogin
  },
  data() {
    return {
      msg: '',
      email: "",
      password: "",
      show: false,
      loading: false,
      recaptchaVisible: true,
      // Reflete o tema salvo (mesmo padrão usado em Logo.vue), para que
      // esta tela acompanhe o tema claro/escuro escolhido pelo usuário -
      // o botão de alternância em si não aparece mais aqui, apenas no
      // menu da conta (AccountMenu), aberto pelo botão de login do
      // cabeçalho.
      isDarkTheme: localStorage.getItem("siteTheme") !== "light",
    };
  },
  created() {
    this.$root.$on("mx3-theme-changed", this.onThemeChanged);
  },
  beforeDestroy() {
    this.$root.$off("mx3-theme-changed", this.onThemeChanged);
  },
  methods: {
    onThemeChanged(isDark) {
      this.isDarkTheme = isDark;
    },
    async onSubmit() {
      this.loading = true;
      this.msg = "";
      try {
        const bodyContent = JSON.stringify({
          username: this.email,
          password: this.password,
        });

        const response = await fetch(window.location.origin + "/api/login.php", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "*/*",
          },
          body: bodyContent,
        });

        let data;
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
            data = await response.json();
        } else {
            const text = await response.text();
            throw new Error("Resposta inválida do servidor: " + text.substring(0, 50));
        }

        if (!response.ok) {
          throw new Error(data.detail || data.message || data.error || data.erro || "Erro ao fazer login");
        }

        localStorage.setItem("accessToken", data.access || data.token || "logged_in");
        this.$api.getAddon();
        
      } catch (err) {
        if (err.message === "No active account found with the given credentials") {
          this.msg = "Nenhuma conta ativa encontrada com as credenciais fornecidas.";
        } else {
          this.msg = "Houve um problema ao realizar login, verifique se o usuário e(ou) senha estão corretos.";
        }
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.auth-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  position: relative;
}

.auth-left {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: linear-gradient(135deg, #0a0e17 0%, #131820 50%, #1a2332 100%);
  position: relative;
  overflow: hidden;
}

.auth-left::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 50%, rgba(0, 212, 255, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(0, 153, 204, 0.06) 0%, transparent 50%);
  pointer-events: none;
}

.auth-wrapper {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 400px;
  background: rgba(20, 28, 40, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(0, 212, 255, 0.15);
  border-radius: 20px;
  padding: 32px 36px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.auth-content {
  display: flex;
  flex-direction: column;
}

.auth-header {
  text-align: center;
  margin-bottom: 24px;
}

.logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  height: 40px;
}

.logo-link {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}



.brand-subtitle {
  font-size: 13px;
  color: #8a95b0;
  font-weight: 400;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 12px; /* Reduzido conforme solicitado */
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 11px;
  font-weight: 700;
  color: #b0b8cc;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.form-input {
  width: 100%;
  padding: 10px 40px 10px 36px;
  background: linear-gradient(135deg, rgba(26, 35, 50, 0.5) 0%, rgba(20, 28, 40, 0.5) 100%);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  outline: none;
  backdrop-filter: blur(10px);
}

.form-input:focus {
  border-color: rgba(0, 212, 255, 0.6);
  box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.15);
}

.input-icon {
  position: absolute;
  left: 10px;
  color: #6b7c8f;
  font-size: 14px;
  pointer-events: none;
}

.toggle-password {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6b7c8f;
  cursor: pointer;
  font-size: 14px;
  padding: 4px 6px;
  z-index: 10;
}

.btn-login {
  width: 100%;
  padding: 10px 16px;
  background: linear-gradient(135deg, #00d4ff 0%, #0099cc 100%);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  color: #000000;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px; /* Espaço reduzido acima do botão */
}

.btn-login:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);
}

.auth-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 12px;
  margin-bottom: 20px;
}

.link-forgot, .link-register {
  color: #00d4ff;
  text-decoration: none;
  font-weight: 600;
}

.social-login-container {
  width: 100%;
  display: flex;
  justify-content: center;
}

/* Customização do Botão Google para ser Full Width e sem fundo branco */
.google-btn-custom {
  width: 100% !important;
}

:deep(.S67oY) { /* Seletor interno do Google Sign-In */
  background-color: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  color: #ffffff !important;
  width: 100% !important;
  border-radius: 8px !important;
}

.auth-right {
  position: relative;
  overflow: hidden;
}

.bg-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('@/assets/img/bg_auth.jpg');
  background-size: cover;
  background-position: center;
}

.overlay-gradient {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, rgba(10, 14, 23, 0.7) 0%, rgba(26, 35, 50, 0.3) 100%);
}

@media (max-width: 1024px) {
  .auth-container { grid-template-columns: 1fr; }
  .auth-right { display: none; }
}

/* ---------------------------------------------------------------------
   Tema Claro
   -----------------------------------------------------------------------
   Antes esta tela era sempre escura (cores fixas em hexadecimal), e só a
   logomarca (mx3-logo, ver Logo.vue) acompanhava o tema. Os blocos abaixo
   dão à tela de Login uma versão clara coerente com a paleta usada no
   resto do site no tema claro (--background-color, --accent-color etc.
   de main-light.css), sem alterar nenhuma regra do tema escuro acima.
--------------------------------------------------------------------- */
.auth-container.theme-light .auth-left {
  background: linear-gradient(135deg, #eaf2fa 0%, #f4f8fc 55%, #ffffff 100%);
}

.auth-container.theme-light .auth-left::before {
  background:
    radial-gradient(circle at 20% 50%, rgba(36, 82, 122, 0.07) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(36, 82, 122, 0.05) 0%, transparent 50%);
}

.auth-container.theme-light .auth-wrapper {
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(36, 82, 122, 0.15);
  box-shadow: 0 8px 32px rgba(13, 27, 42, 0.1);
}

.auth-container.theme-light .brand-subtitle {
  color: #5b6b7c;
}

.auth-container.theme-light .form-label {
  color: #3b4a5a;
}

.auth-container.theme-light .form-input {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(234, 242, 250, 0.7) 100%);
  border: 1px solid rgba(36, 82, 122, 0.25);
  color: #0d1b2a;
}

.auth-container.theme-light .form-input::placeholder {
  color: #8a95a8;
}

.auth-container.theme-light .form-input:focus {
  border-color: rgba(36, 82, 122, 0.6);
  box-shadow: 0 0 0 3px rgba(36, 82, 122, 0.15);
}

.auth-container.theme-light .input-icon,
.auth-container.theme-light .toggle-password {
  color: #6b7c8f;
}

.auth-container.theme-light .btn-login {
  background: linear-gradient(135deg, #24527a 0%, #183b5b 100%);
  border: 1px solid rgba(36, 82, 122, 0.3);
  color: #ffffff;
}

.auth-container.theme-light .btn-login:hover:not(:disabled) {
  box-shadow: 0 4px 15px rgba(36, 82, 122, 0.3);
}

.auth-container.theme-light .link-forgot,
.auth-container.theme-light .link-register {
  color: #24527a;
}

.auth-container.theme-light :deep(.S67oY) {
  background-color: rgba(13, 27, 42, 0.03) !important;
  border: 1px solid rgba(13, 27, 42, 0.12) !important;
  color: #0d1b2a !important;
}

.auth-container.theme-light .overlay-gradient {
  background: linear-gradient(90deg, rgba(234, 242, 250, 0.75) 0%, rgba(36, 82, 122, 0.25) 100%);
}
</style>
